import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, MessageSquare, Shield, ShieldOff } from 'lucide-react';
import { api, type ChatDmPartnerProfile } from '@/lib/api';
import { useAuth } from '@/auth';
import { cn } from '@/lib/utils';
import { UserAvatar } from './UserAvatar';

interface UserProfileModalProps {
  userId: string;
  onClose: () => void;
  onOpenDm?: (userId: string) => void;
}

export function UserProfileModal({ userId, onClose, onOpenDm }: UserProfileModalProps) {
  const { user: currentUser } = useAuth();
  const backdropRef = useRef<HTMLDivElement>(null);

  const [profile, setProfile] = useState<ChatDmPartnerProfile | null>(null);
  const [isBlocked, setIsBlocked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [blockLoading, setBlockLoading] = useState(false);

  const isSelf = currentUser?.userId === userId;

  // Reset all derived state whenever the target user changes, then fetch fresh.
  useEffect(() => {
    let cancelled = false;
    setProfile(null);
    setIsBlocked(false);
    setFetchError(false);
    setLoading(true);

    Promise.all([api.getChatUserProfile(userId), api.listChatBlockedUsers()])
      .then(([profileRes, blocksRes]) => {
        if (cancelled) return;
        const p = profileRes.profile;
        setProfile(p);
        setIsBlocked(blocksRes.blocks.some((b) => b.blockedId === userId));
      })
      .catch(() => {
        if (!cancelled) setFetchError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [userId]);

  // Close on Escape.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler, true);
    return () => document.removeEventListener('keydown', handler, true);
  }, [onClose]);

  const handleBlockToggle = async () => {
    setBlockLoading(true);
    try {
      if (isBlocked) {
        await api.unblockChatUser(userId);
        setIsBlocked(false);
      } else {
        await api.blockChatUser(userId);
        setIsBlocked(true);
      }
    } catch {
      // non-critical
    } finally {
      setBlockLoading(false);
    }
  };

  // Derive display values — use persisted displayName if set, else fall back gracefully.
  const displayName = profile?.displayName || null;
  const username = profile?.username || null;
  const bio = profile?.bio || null;
  const bannerColor = profile?.bannerColor ?? '#cbbfb5';
  const avatarColor = profile?.avatarColor ?? '#5865F2';

  // Avatar initial — pick the first meaningful character available.
  const avatarInitial = (displayName || username || userId).charAt(0).toUpperCase();

  return createPortal(
    <div
      ref={backdropRef}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onPointerDown={(e) => {
        if (e.target === backdropRef.current) onClose();
      }}
    >
      <div className="relative w-[320px] rounded-2xl overflow-hidden bg-card shadow-2xl flex flex-col">
        {/* ── Banner (persisted bannerColor from chat settings) ── */}
        <div
          className="h-[100px] w-full flex-shrink-0 transition-colors duration-300"
          style={{ backgroundColor: bannerColor }}
        />

        {/* ── Close button ── */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 h-7 w-7 rounded-full bg-black/30 hover:bg-black/50 flex items-center justify-center transition-colors"
        >
          <X className="h-4 w-4 text-white" />
        </button>

        {/* ── Avatar — floats over the banner/body seam ── */}
        <div className="px-4 pt-0 -mt-9 relative z-10 flex items-end gap-3 mb-2">
          <UserAvatar
            userId={userId}
            displayName={displayName ?? 'avatar'}
            initial={avatarInitial}
            className="h-[72px] w-[72px] border-4 border-card transition-colors duration-300"
            fallbackClassName="text-2xl font-bold text-white"
            background={avatarColor}
          />
        </div>

        {/* ── Profile text ── */}
        <div className="px-4 pb-4">
          {loading ? (
            <div className="space-y-2 py-1">
              <div className="h-4 w-36 bg-muted animate-pulse rounded" />
              <div className="h-3 w-24 bg-muted animate-pulse rounded" />
            </div>
          ) : fetchError ? (
            <p className="text-sm text-muted-foreground py-1">Could not load profile.</p>
          ) : (
            <div className="space-y-1">
              {/* Display name */}
              <p className="text-[15px] font-bold text-foreground leading-tight">
                {displayName || username || userId}
              </p>

              {/* Username — shown as subtitle only when it differs from displayName */}
              {username && displayName && username !== displayName && (
                <p className="text-sm text-muted-foreground leading-none">{username}</p>
              )}

              {/* Bio — shown only when the user has written one */}
              {bio && (
                <p className="mt-2 text-[13px] text-foreground/75 leading-snug line-clamp-4 whitespace-pre-line">
                  {bio}
                </p>
              )}
            </div>
          )}

          {/* ── Actions ── */}
          {!isSelf && (
            <div className="mt-4 flex items-center gap-2">
              {onOpenDm && (
                <button
                  type="button"
                  onClick={() => {
                    onOpenDm(userId);
                    onClose();
                  }}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
                >
                  <MessageSquare className="h-4 w-4" />
                  Message
                </button>
              )}
              <button
                type="button"
                onClick={handleBlockToggle}
                disabled={blockLoading || loading}
                className={cn(
                  'flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50',
                  isBlocked
                    ? 'bg-muted text-foreground hover:bg-muted/80'
                    : 'bg-destructive/10 text-destructive hover:bg-destructive/20',
                )}
              >
                {isBlocked ? (
                  <>
                    <Shield className="h-4 w-4" />
                    Unblock
                  </>
                ) : (
                  <>
                    <ShieldOff className="h-4 w-4" />
                    Block
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
